// Mail V2 — 顶部 header(标题 + 面包屑 + 搜索 + 账号下拉)。
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Settings as SettingsIcon } from "lucide-react";
import { type MailAccount } from "../data/demoMailData";

interface MailV2HeaderProps {
  selectedAccount: MailAccount;
  accounts: MailAccount[];
  onSelectAccount: (id: string) => void;
  searchQuery: string;
  onSearchChange: (s: string) => void;
  // Stage Mail-V2 Step 20: account dialog entry points
  onAddAccount?: () => void;
  onManageAccounts?: () => void;
}

export function MailV2Header({
  selectedAccount,
  accounts,
  onSelectAccount,
  searchQuery,
  onSearchChange,
  onAddAccount,
  onManageAccounts,
}: MailV2HeaderProps) {
  const [isAccountOpen, setIsAccountOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAccountOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isAccountOpen]);

  const personal = (accounts ?? []).filter((a) => a?.accountType === "personal");
  const shared = (accounts ?? []).filter((a) => a?.accountType === "shared");
  const other = (accounts ?? []).filter((a) => a?.accountType === "other");

  const renderAccountItem = (a: MailAccount) => {
    const isActive = a?.id === selectedAccount?.id;
    const hasError = a?.lastSyncStatus === "error";
    return (
      <div
        key={a.id}
        className={`mv2-account-item${isActive ? " active" : ""}`}
        onClick={() => {
          onSelectAccount(a.id);
          setIsAccountOpen(false);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectAccount(a.id);
            setIsAccountOpen(false);
          }
        }}
      >
        <div className="mv2-account-trigger-icon" style={{ width: 24, height: 24, fontSize: 11 }}>
          {a.displayName.charAt(0).toUpperCase()}
        </div>
        <span>{a.email}</span>
        {/* Mail V2.5 (2026-08-20): account-level error indicator (locked plan §16.9 §D) */}
        {hasError ? (
          <span
            className="mv2-account-error-dot"
            title="同期に失敗しました"
            aria-label="同期エラー"
          />
        ) : null}
        {isActive && <span className="mv2-account-check">✓</span>}
      </div>
    );
  };

  return (
    <div className="mv2-topbar">
      <div>
        <div className="mv2-topbar-title">メール対応</div>
        <div className="mv2-topbar-breadcrumb">ホーム / メール対応</div>
      </div>
      <input
        className="mv2-topbar-search"
        type="text"
        placeholder="メールを検索"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        aria-label="メール検索"
      />
      <div className="mv2-topbar-spacer" />
      <div className="mv2-account-switcher" ref={dropdownRef}>
        <button
          className="mv2-account-trigger"
          onClick={() => setIsAccountOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isAccountOpen}
          title="アカウント切り替え"
        >
          <div className="mv2-account-trigger-icon">
            {selectedAccount.displayName.charAt(0).toUpperCase()}
          </div>
          <span>現在のアカウント: {selectedAccount.email}</span>
          <ChevronDown size={14} />
        </button>
        {isAccountOpen && (
          <div className="mv2-account-menu" role="listbox">
            <div className="mv2-account-section-label">個人メール</div>
            {personal.map(renderAccountItem)}
            <div className="mv2-account-divider" />
            <div className="mv2-account-section-label">共有メール</div>
            {shared.map(renderAccountItem)}
            {other.length > 0 && (
              <>
                <div className="mv2-account-divider" />
                <div className="mv2-account-section-label">その他</div>
                {other.map(renderAccountItem)}
              </>
            )}
            <div className="mv2-account-divider" />
            <div
              className="mv2-account-add"
              role="button"
              tabIndex={0}
              onClick={() => {
                setIsAccountOpen(false);
                onAddAccount?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsAccountOpen(false);
                  onAddAccount?.();
                }
              }}
            >
              <Plus size={14} />
              メールアカウントを追加
            </div>
            <div
              className="mv2-account-manage"
              role="button"
              tabIndex={0}
              onClick={() => {
                setIsAccountOpen(false);
                onManageAccounts?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsAccountOpen(false);
                  onManageAccounts?.();
                }
              }}
            >
              <SettingsIcon size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
              アカウント管理
            </div>
          </div>
        )}
      </div>
    </div>
  );
}